import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { SEGMENTS, DEFAULT_SEGMENT, SegmentId } from './data';
import {
  trackMetaEvent,
  trackMetaCustomEvent,
  setMetaAdvancedMatching,
} from '../../utils/metaPixel';
import './index.scss';

type TasterPhase = 'quiz' | 'demog' | 'result' | 'confirm';

// Session ID stored in sessionStorage so it survives React StrictMode's
// double-mount in development (useRef gets a fresh value on each mount, but
// sessionStorage persists for the browser tab's lifetime).
function getOrCreateSessionId(): string {
  const key = 'moodra_pilot_session';
  const stored = sessionStorage.getItem(key);
  if (stored) return stored;
  const id = Math.random().toString(36).substring(2, 9) + Date.now().toString(36);
  sessionStorage.setItem(key, id);
  return id;
}

export const PilotPage = () => {
  const location = useLocation();

  // Parse URL params
  const searchParams = new URLSearchParams(location.search);
  const rawSeg = searchParams.get('seg') as SegmentId | null;
  const segId: SegmentId = rawSeg && SEGMENTS[rawSeg] ? rawSeg : DEFAULT_SEGMENT;
  const seg = SEGMENTS[segId];

  const utmSource = searchParams.get('utm_source') || '';
  const utmMedium = searchParams.get('utm_medium') || '';
  const utmCampaign = searchParams.get('utm_campaign') || '';

  // Session ID: stable for the whole browser tab session
  const sessionId = useRef<string>(getOrCreateSessionId());

  // Serial queue: each sendSignal call waits for the previous one to finish
  // before executing. This prevents the race condition where concurrent reads
  // from Google Sheets all return "no row found" and each appends a new row.
  const signalQueue = useRef<Promise<void>>(Promise.resolve());

  // Taster flow state
  const [step, setStep] = useState(0); // 0–4 = Q1–Q5 active index
  const [phase, setPhase] = useState<TasterPhase>('quiz');
  const [tasterHeading, setTasterHeading] = useState(seg.taster.heading);

  // Demographics
  const [age, setAge] = useState('');
  const [gender, setGender] = useState('');

  // Email capture
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [qualitative, setQualitative] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Scroll reveal
  const revealRefs = useRef<HTMLElement[]>([]);

  // Section refs for scroll-to CTA
  const tasterRef = useRef<HTMLElement | null>(null);
  const recogRef = useRef<HTMLElement | null>(null);

  // Track which signals have already been sent to avoid duplicates
  const signalsSent = useRef<Set<string>>(new Set());

  // Reset heading + step when segment changes (e.g. user changes ?seg= in URL)
  useEffect(() => {
    setStep(0);
    setPhase('quiz');
    setTasterHeading(seg.taster.heading);
    setAge('');
    setGender('');
    setEmail('');
    setConsent(false);
    setQualitative('');
    setSubmitted(false);
    signalsSent.current.clear();
  }, [segId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update page title
  useEffect(() => {
    document.title = seg.title;
  }, [seg.title]);

  // Fire analytics signal — enqueued so calls execute one-at-a-time.
  // This prevents the race condition where concurrent Google Sheets reads all
  // return "row not found" and each creates a duplicate row instead of updating.
  const sendSignal = useCallback(
    (data: Record<string, unknown>) => {
      signalQueue.current = signalQueue.current.then(async () => {
        try {
          await fetch('/api/signals', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              segment: seg.id,
              session_id: sessionId.current,
              utm_source: utmSource,
              utm_medium: utmMedium,
              utm_campaign: utmCampaign,
              ...data,
            }),
          });
        } catch {
          // Fail silently — never block the user flow for an analytics error
        }
      });
    },
    [seg.id, utmSource, utmMedium, utmCampaign],
  );

  // Track hero view once on mount
  useEffect(() => {
    if (!signalsSent.current.has('hero_view')) {
      signalsSent.current.add('hero_view');
      sendSignal({ step_reached: 'hero_view', clicked_priced_button: false });
    }
  }, [sendSignal]);

  // Scroll reveal observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('pp-seen');

            // Track scroll milestones
            const id = (entry.target as HTMLElement).dataset.trackId;
            if (id && !signalsSent.current.has(id)) {
              signalsSent.current.add(id);
              sendSignal({ step_reached: id, clicked_priced_button: false });
            }
          }
        });
      },
      { threshold: 0.14 },
    );

    revealRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [sendSignal]);

  const addRevealRef = (el: HTMLElement | null) => {
    if (el && !revealRefs.current.includes(el)) {
      revealRefs.current.push(el);
    }
  };

  // Scroll helpers
  const scrollToTaster = () => tasterRef.current?.scrollIntoView({ behavior: 'smooth' });
  const scrollToRecog = () => recogRef.current?.scrollIntoView({ behavior: 'smooth' });

  // Taster handlers
  const handleAnswer = (questionIndex: number) => {
    // Fire once when the very first question is answered — this is the moment
    // the visitor engages with the taster, our top-of-funnel intent signal.
    if (questionIndex === 0 && !signalsSent.current.has('meta_taster_started')) {
      signalsSent.current.add('meta_taster_started');
      trackMetaCustomEvent('TasterStarted', { content_name: 'pilot_taster', segment: seg.id });
    }
    const nextStep = questionIndex + 1;
    if (nextStep < 5) {
      setStep(nextStep);
    } else {
      // All 5 questions answered → show demographics
      setPhase('demog');
      setStep(5);
    }
  };

  const handleDemogContinue = () => {
    sendSignal({
      step_reached: 'taster_completed',
      clicked_priced_button: false,
      age_range: age,
      gender,
    });
    trackMetaCustomEvent('TasterCompleted', { content_name: 'pilot_taster', segment: seg.id });
    setPhase('result');
    setTasterHeading("Here's the beginning of what we see.");
  };

  const handlePriceClick = () => {
    if (!signalsSent.current.has('payment_clicked')) {
      signalsSent.current.add('payment_clicked');
      sendSignal({
        step_reached: 'payment_clicked',
        clicked_priced_button: true,
        age_range: age,
        gender,
      });
      // Strongest intent signal in the funnel — optimize ad delivery for this.
      // Pre-launch: this is a waitlist click, so InitiateCheckout (not Purchase).
      trackMetaEvent('InitiateCheckout', {
        content_name: 'pilot_full_report',
        content_category: seg.id,
        currency: 'USD',
        value: 29,
      });
    }
    setPhase('confirm');
    setTasterHeading('Thank you — that click mattered.');
  };

  const handleEmailSubmit = () => {
    setSubmitting(true);
    sendSignal({
      step_reached: 'email_submitted',
      clicked_priced_button: true,
      email: email && consent ? email : null,
      qualitative_answer: qualitative || null,
      age_range: age,
      gender,
    });
    // Advanced Matching: attach the hashed email (only with consent) so Meta
    // can attribute this Lead more accurately, then fire the Lead event.
    if (email && consent) {
      setMetaAdvancedMatching(email);
    }
    trackMetaEvent('Lead', { content_name: 'pilot_waitlist', content_category: seg.id });
    // Show confirmation immediately — don't make the user wait for the API
    setSubmitted(true);
    setSubmitting(false);
  };

  // Progress bar: filled count = answered questions (0–5)
  const filledBars = Math.min(step, 5);

  // CSS custom properties for per-segment theming
  const cssVars = {
    '--paper': seg.paper,
    '--paper-deep': seg.paperDeep,
    '--line': seg.lineColor,
  } as React.CSSProperties;

  return (
    <div className="pilot-page" style={cssVars}>
      {/* ---- Top bar ---- */}
      <div className="pp-wrap">
        <div className="pp-topbar">
          <img className="pp-logo" src="/logo.svg" alt="Moodra" />
          <span className="pp-meta">A self-awareness platform</span>
        </div>
      </div>

      {/* ---- Hero ---- */}
      <section className="pp-hero">
        <div className="pp-wrap">
          <div className="pp-eyebrow">{seg.hero.eyebrow}</div>
          <h1 className="pp-h1">
            <span className="pp-h1-a">{seg.hero.headlineA}</span>
            <span className="pp-h1-b">{seg.hero.headlineB}</span>
          </h1>
          <p className="pp-sub">{seg.hero.sub}</p>
          <div className="pp-cta-row">
            <button className="pp-btn" onClick={scrollToTaster}>
              {seg.hero.primaryCta} <span className="pp-arr">→</span>
            </button>
            <button className="pp-btn pp-btn-ghost" onClick={scrollToRecog}>
              First, see if this is you
            </button>
          </div>

          {/* Character figure — desktop only */}
          <div className="pp-figure" aria-hidden="true">
            <img className="pp-figure-img" src={seg.figure} alt="" />
          </div>
        </div>
      </section>

      {/* ---- Recognition ---- */}
      <section
        ref={(el) => {
          if (el) recogRef.current = el;
          addRevealRef(el);
        }}
        className="pp-band pp-band-deep pp-reveal"
        data-track-id="scroll_recognition"
        id="recog"
      >
        <div className="pp-wrap">
          <div className="pp-kicker">{seg.recognition.kicker}</div>
          <h2 className="pp-recog-h2">{seg.recognition.heading}</h2>
          <ul className="pp-recog-list">
            {seg.recognition.bullets.map((bullet, i) => (
              <li key={i}>
                <span className="pp-n">0{i + 1}</span>
                <span>{bullet}</span>
              </li>
            ))}
            <li className="pp-closer">{seg.recognition.closer}</li>
          </ul>
        </div>
      </section>

      {/* ---- Mechanism ---- */}
      <section
        ref={addRevealRef}
        className="pp-band pp-reveal"
        data-track-id="scroll_mechanism"
      >
        <div className="pp-wrap">
          <div className="pp-kicker">{seg.mechanism.kicker}</div>
          <div className="pp-mech">
            <div className="pp-mech-big">
              {seg.mechanism.bigNormal1}
              <em>{seg.mechanism.bigItalic1}</em>
              {seg.mechanism.bigNormal2}
              <em>{seg.mechanism.bigItalic2}</em>
            </div>
            <div className="pp-mech-body">
              <p>{seg.mechanism.p1}</p>
              <p>{seg.mechanism.p2}</p>
              <p className="pp-credibility">
                Developed by experts from British Columbia, UC Berkeley and Georgetown.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ---- Taster + Paywall + Confirm ---- */}
      <section
        ref={(el) => {
          if (el) tasterRef.current = el;
          addRevealRef(el);
        }}
        className="pp-band pp-band-deep pp-reveal"
        data-track-id="taster_started"
        id="taster"
      >
        <div className="pp-wrap">
          <div className="pp-taster-shell">
            <div className="pp-kicker">A quick taste — 5 questions, 2 minutes</div>
            <h2 className="pp-taster-heading">{tasterHeading}</h2>

            {/* Progress bars — only shown during quiz/demog */}
            {(phase === 'quiz' || phase === 'demog') && (
              <div className="pp-progress">
                {Array.from({ length: 5 }, (_, i) => (
                  <span key={i} className={i < filledBars ? 'on' : ''} />
                ))}
              </div>
            )}

            <div className="pp-q-area">
              {/* Questions 1–5 */}
              {phase === 'quiz' &&
                seg.taster.questions.map((q, qi) =>
                  qi === step ? (
                    <div key={qi}>
                      <div className="pp-q-num">Question {qi + 1} of 5</div>
                      <div className="pp-q-text">{q.text}</div>
                      <div className="pp-opts">
                        {q.options.map((opt, oi) => (
                          <button
                            key={oi}
                            className="pp-opt"
                            onClick={() => handleAnswer(qi)}
                          >
                            {opt}
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : null,
                )}

              {/* Demographics */}
              {phase === 'demog' && (
                <div>
                  <div className="pp-q-num">One last thing</div>
                  <div className="pp-q-text">
                    Two quick details so the report can speak to you, not at you.
                  </div>
                  <div className="pp-demog">
                    <div className="pp-demog-field">
                      <label htmlFor="pp-age">Your age</label>
                      <select
                        id="pp-age"
                        value={age}
                        onChange={(e) => setAge(e.target.value)}
                      >
                        <option value="" disabled>
                          Select age range
                        </option>
                        <option value="18-24">18–24</option>
                        <option value="25-34">25–34</option>
                        <option value="35-44">35–44</option>
                        <option value="45-54">45–54</option>
                        <option value="55+">55+</option>
                        <option value="prefer-not-to-say">Prefer not to say</option>
                      </select>
                    </div>
                    <div className="pp-demog-field">
                      <label htmlFor="pp-gender">Your gender</label>
                      <select
                        id="pp-gender"
                        value={gender}
                        onChange={(e) => setGender(e.target.value)}
                      >
                        <option value="" disabled>
                          Select gender
                        </option>
                        <option value="woman">Woman</option>
                        <option value="man">Man</option>
                        <option value="non-binary">Non-binary</option>
                        <option value="prefer-not-to-say">Prefer not to say</option>
                      </select>
                    </div>
                    <button
                      className="pp-demog-continue"
                      onClick={handleDemogContinue}
                      disabled={!age || !gender}
                    >
                      Continue →
                    </button>
                  </div>
                </div>
              )}

              {/* Result / Paywall */}
              {phase === 'result' && (
                <div className="pp-result">
                  <div className="pp-rk">Your pattern is forming</div>
                  <h3>{seg.result.heading}</h3>
                  <p>{seg.result.body}</p>
                  <button className="pp-price-btn" onClick={handlePriceClick}>
                    Unlock my full report — $29/mo
                  </button>
                  <div className="pp-price-note">Cancel anytime. One report, one clear path.</div>
                </div>
              )}

              {/* Honest waitlist confirm */}
              {phase === 'confirm' && (
                <div className="pp-confirm">
                  <span className="pp-badge">You're early</span>
                  <h3>You're early — and that's a good thing.</h3>
                  <p>
                    Moodra isn't fully live yet. You're now on the founding list — you'll get in
                    first, at this price, before anyone else. We'll email you the moment it's ready.
                  </p>
                  <p className="pp-small">
                    And thank you. Clicking that button just told us something genuinely important.
                  </p>

                  {!submitted ? (
                    <div className="pp-qcap">
                      <div className="pp-field">
                        <label htmlFor="pp-email">
                          Want first access when we go live? Leave your email — totally optional.
                        </label>
                        <input
                          type="email"
                          id="pp-email"
                          placeholder="you@example.com"
                          autoComplete="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                        />
                      </div>
                      <div className="pp-consent">
                        <input
                          type="checkbox"
                          id="pp-consent"
                          checked={consent}
                          onChange={(e) => setConsent(e.target.checked)}
                        />
                        <label htmlFor="pp-consent">
                          I agree to be contacted about Moodra updates. We only email when there's
                          something real to share.{' '}
                          <a href="/#/privacy-policy">Privacy Policy</a>
                        </label>
                      </div>
                      <div className="pp-field">
                        <label htmlFor="pp-qual">
                          One quick thing — what made you click? (optional, but really helps us)
                        </label>
                        <textarea
                          id="pp-qual"
                          placeholder="Type as much or as little as you like…"
                          value={qualitative}
                          onChange={(e) => setQualitative(e.target.value)}
                        />
                      </div>
                      <button
                        className="pp-send-btn"
                        onClick={handleEmailSubmit}
                        disabled={submitting}
                      >
                        {submitting ? 'Sending…' : 'Send →'}
                      </button>
                    </div>
                  ) : (
                    <div className="pp-thanks">
                      Got it — thank you. This genuinely helps.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ---- Footer ---- */}
      <footer className="pp-footer">
        <div className="pp-wrap pp-foot">
          <img className="pp-footer-logo" src="/logo.svg" alt="Moodra" />
          <p className="pp-footer-note">{seg.footerNote}</p>
          <a href="/#/privacy-policy" className="pp-privacy-link">
            Privacy Policy
          </a>
        </div>
      </footer>
    </div>
  );
};

export default PilotPage;
